import { useAppDispatch } from "@/app/hooks/reduxHooks"
import { openCompanyDetails } from "@/features/searchTable/slice/companyDetailsSlice"
import { closeContactInfo, openContactInfoForCompany } from "@/features/searchTable/slice/contactInfoSlice"
import { CompanyAttributes, ContactAttributes } from "@/interface/searchTable/search"
import { Building, Eye, List, Mail, Phone, User, X } from "lucide-react"
import { useState } from "react"
import LinkIcon from "../../static/media/icons/link-m.svg?react"
import LinkedinIcon from "../../static/media/icons/linkedin_icon.svg?react"
import NetflixLogo from "../../static/media/logo/netflix-logo.svg?react"
import { Avatar } from "./avatar"
import { Badge } from "./badge"
import { Button } from "./button"
import EmployeesDetails from "./employeeDetails"

type ContactInformationProps = {
  contact: ContactAttributes | null
  company: CompanyAttributes | null
  onClose: () => void
  hideContactFields?: boolean
  hideCompanyActions?: boolean
}

const ContactInformation = ({ contact, onClose, hideContactFields = false, hideCompanyActions = false, company }: ContactInformationProps) => {
  const dispatch = useAppDispatch()
  const [revealedFields, setRevealedFields] = useState<Set<string>>(new Set())

  const toggleReveal = (field: string) => {
    const updated = new Set(revealedFields)
    if (updated.has(field)) {
      updated.delete(field)
    } else {
      updated.add(field)
    }
    setRevealedFields(updated)
  }

  const revealAllInfo = () => {
    setRevealedFields(new Set(["phone", "email"]))
  }

  const maskEmail = (email: string) => {
    if (!email) return ""
    const [username, domain] = email.split("@")
    const maskedUsername = "• • • •"
    return `${maskedUsername} @${domain}`
  }

  const maskPhone = (phone: string) => {
    if (!phone) return ""
    const lastFour = phone.slice(-4)
    return `• • • • ${lastFour}`
  }

  return (
    <div className="flex min-h-0 w-[424px] flex-col  border-l border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b p-5">
        <span className="text-base font-medium">Contact Information</span>
        <Button variant="ghost" className="p-0" onClick={onClose} aria-label="Close">
          <X className="size-4" />
        </Button>
      </div>
      <div className="max-h-[calc(100vh-100px)] overflow-y-auto">
        {/* Contact */}

        {!hideContactFields && (
          <div className="flex flex-col gap-5 p-5">
            <div className="flex flex-row items-center gap-4">
              <Avatar className="flex size-10 items-center justify-center rounded-full border">
                <User className="size-5" />
              </Avatar>
              <div className="flex flex-col">
                <span className="text-base font-medium text-gray-950">{contact?.full_name}</span>
                <span className="text-sm font-normal text-gray-600">{contact?.title}</span>
              </div>
            </div>

            <div className=" flex w-full flex-row items-center gap-3">
              <Button
                onClick={revealAllInfo}
                variant="outline"
                className="w-full rounded-lg bg-[#335CFF] p-1.5 text-sm font-medium text-white hover:bg-[#335CFF] hover:text-white"
              >
                Reveal all info
              </Button>
              <Button
                variant="outline"
                className="w-full rounded-lg bg-white p-1.5 text-sm font-medium text-[#5C5C5C] hover:bg-white hover:text-[#5C5C5C]"
                onClick={() => {
                  dispatch(openCompanyDetails(null))
                  dispatch(closeContactInfo())
                }}
              >
                <User className="size-5 text-[#5C5C5C]" /> View full profile
              </Button>

              {!!contact?.linkedin_url && (
                <a
                  href={contact?.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center rounded-xl border border-gray-200 bg-transparent p-3 text-blue-600 transition-colors hover:bg-gray-50"
                >
                  <Button className="size-3 bg-transparent p-2 hover:bg-transparent">
                    <LinkedinIcon className="size-5" />
                  </Button>
                </a>
              )}
            </div>
          </div>
        )}
        {!!hideContactFields && (
          <div className="flex flex-col gap-5 p-5">
            <div className="flex flex-row items-center gap-4">
              <Avatar className="flex size-10 items-center justify-center rounded-full border">
                <NetflixLogo className="size-5" />
              </Avatar>
              <div className="flex flex-col">
                <span className="text-base font-medium text-gray-950">{company?.company}</span>
                <span className="text-sm font-normal text-gray-600">{company?.location.country}</span>
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <Button
                variant="outline"
                className="flex w-full items-center justify-center rounded-lg border-[#335CFF] bg-transparent p-1.5 text-[#335CFF] hover:bg-transparent hover:text-[#335CFF]"
              >
                Add company as filter
              </Button>
              <Button variant="outline" className="flex items-center justify-center bg-transparent p-1.5 hover:bg-transparent">
                <LinkedinIcon className="size-5" />
              </Button>
            </div>
          </div>
        )}
        {/* phone number */}
        {!hideContactFields && (
          <div className="flex flex-col gap-5 pb-5">
            <div className="w-full bg-[#F7F7F7] px-5 py-1.5">
              <span className=" text-xs font-medium text-gray-400">Phone</span>
            </div>
            <div className="space-y-3 px-5 py-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Phone className="size-[18px] text-gray-600" />
                  <span className="text-sm font-normal text-[#5C5C5C]">Primary Phone</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-950">
                    {revealedFields.has("phone") ? "+917550264569" : maskPhone("+917550264569")}
                  </span>
                  <button onClick={() => toggleReveal("phone")} className="text-blue-500 transition-colors hover:text-blue-600">
                    <Eye className="size-[18px]" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* email */}
        {!hideContactFields && !!contact?.email && (
          <div className="flex flex-col gap-5 pb-5">
            <div className="w-full bg-[#F7F7F7] px-5 py-1.5">
              <span className=" text-xs font-medium text-gray-400">Email</span>
            </div>
            <div className="space-y-3 px-5 py-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="size-[18px] text-gray-600" />
                  <span className="text-sm font-normal text-[#5C5C5C]">Official Email</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-950">{revealedFields.has("email") ? contact.email : maskEmail(contact.email)}</span>
                  <button onClick={() => toggleReveal("email")} className="text-blue-500 transition-colors hover:text-blue-600">
                    <Eye className="size-[18px]" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* company */}
        <div className="flex flex-col ">
          <div className="w-full bg-[#F7F7F7] px-5 py-1.5">
            <span className=" text-xs font-medium text-gray-400">Company Information</span>
          </div>
          <div className="p-5 pt-3">
            {!hideContactFields && (
              <>
                <div className="flex flex-row items-center">
                  <div className="flex w-full flex-row items-center justify-between py-2">
                    <div className="flex flex-row items-center gap-3">
                      <Avatar className="flex size-10 items-center justify-center rounded-full border">
                        <Building className="size-5" />
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-950">{contact?.company}</span>
                        <span className="text-xs font-normal text-gray-600">San Francisco, US</span>
                      </div>
                    </div>
                    <button className="cursor-pointer">
                      <LinkIcon className="size-5" />
                    </button>
                  </div>
                </div>
                <div className="flex flex-row gap-3 py-3">
                  <Button
                    variant="outline"
                    className="w-full rounded-[8px] border-[#335CFF] bg-white p-1.5 text-sm font-medium text-[#335CFF] hover:bg-white hover:text-[#335CFF]"
                    onClick={() => {
                      // Close local/global panel if provided, then open company-mode in the global panel
                      onClose?.()
                      const companyFromContact: CompanyAttributes = {
                        _id: "",
                        website: contact?.website || "",
                        company: contact?.company || "",
                        company_linkedin_url: null,
                        industry: "",
                        keywords: [],
                        location: { country: "" },
                        company_size: ""
                      }
                      dispatch(openContactInfoForCompany(companyFromContact))
                    }}
                  >
                    View Company
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full rounded-[8px] border-[#EBEBEB] bg-white p-1.5 text-sm font-medium text-[#5C5C5C] hover:bg-white hover:text-[#5C5C5C]"
                  >
                    <List className="size-5 text-[#5C5C5C]" />
                    Add to list
                  </Button>

                  {!!contact?.linkedin_url && (
                    <a href={contact?.linkedin_url} target="_blank" rel="noopener noreferrer">
                      <Button className="size-8 rounded-lg border bg-transparent p-1.5 hover:bg-transparent">
                        <LinkedinIcon className="size-5" />
                      </Button>
                    </a>
                  )}
                </div>
              </>
            )}

            <div className="space-y-3">
              {/* Business - SaaS */}
              <div className="flex justify-between">
                <span className="text-sm font-normal text-[#5C5C5C]">Business</span>
                <span className="text-sm font-medium text-gray-950">SaaS</span>
              </div>

              {/* Employees */}
              <div className="flex justify-between">
                <span className="text-sm font-normal text-[#5C5C5C]">Employees</span>
                <span className="text-sm font-medium text-gray-950">201-500</span>
              </div>

              {/* Industry */}
              <div className="flex justify-between">
                <span className="text-sm font-normal text-[#5C5C5C]">Industry</span>
                <span className="text-sm font-medium text-gray-950">Manufacturing</span>
              </div>

              {/* Revenue */}
              <div className="flex justify-between">
                <span className="text-sm font-normal text-[#5C5C5C]">Revenue</span>
                <span className="text-sm font-medium text-gray-950">$50M-$100M</span>
              </div>

              {/* Year Found */}
              <div className="flex justify-between">
                <span className="text-sm font-normal text-[#5C5C5C]">Year Found</span>
                <span className="text-sm font-medium text-gray-950">1995</span>
              </div>
            </div>
          </div>
        </div>

        {/* companyinfo */}
        <div className="p-5 pt-0">
          <div className="rounded-[12px] border border-[#D1D1D1] p-4">
            <span className="text-base font-normal text-[#5C5C5C]">
              Netflix is one of the world&apos;s leading entertainment services, with over 300 million paid memberships in over 190 countries enjoying
              TV series, films and games across a wide variety of genres and languages. Members can play, pause and resume watching as much as they
              want, anytime, anywhere, and can change their plans at any time.
            </span>
          </div>
        </div>

        {/* Employee Details */}
        <div className="flex flex-col gap-5 pb-5">
          <div className="w-full bg-[#F7F7F7] px-5 py-1.5">
            <span className=" text-xs font-medium text-gray-400">Employee Details</span>
          </div>
          <div className="px-5">
            <EmployeesDetails />
          </div>
        </div>

        {/* Specialties */}
        <div className="flex flex-col gap-5 pb-5">
          <div className="w-full bg-[#F7F7F7] px-5 py-1.5">
            <span className=" text-xs font-medium text-gray-400">Specialties</span>
          </div>
          <div className="px-5">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                Product Strategy
              </Badge>
              <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                Digital Transformation
              </Badge>
              <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                A/B Testing
              </Badge>
              <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                Content Strategy
              </Badge>
              <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                User Experience
              </Badge>
              <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                Data Analytics
              </Badge>
              <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                Growth Marketing
              </Badge>
              <Badge variant="secondary" className="border border-border bg-white px-2 py-1 text-sm font-medium text-gray-600 hover:bg-white">
                Platform Development
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContactInformation
